import { toRaw } from 'vue'
import type { Station } from '@/types/stationTypes'

// The station list is replaced, never mutated, so one index per list stays valid. Keyed on the raw
// array so the store's reactive proxy and the plain array share one index.
const indexes = new WeakMap<Station[], Map<string, Station>>()

export const findStation = (code: string, allStations: Station[]): Station | undefined => {
  const raw = toRaw(allStations)
  let index = indexes.get(raw)
  if (!index) {
    index = new Map(raw.map((s) => [s._u_i_c_station_code, s]))
    indexes.set(raw, index)
  }
  return index.get(code)
}

// The code a stop is searched by: its geographical agglomeration's code when it belongs to one,
// otherwise its own. A search from a member stop only covers that stop, so the return leg would
// miss departures from the other stops in the group. Commercial agglomerations are tariff groups,
// not places to travel from, so their stops keep their own code.
export const getSearchStationCode = (station: Station, allStations: Station[]): string => {
  const code = station._u_i_c_station_code
  const parentCode = station.parent ?? findStation(code, allStations)?.parent
  const parent = parentCode ? findStation(parentCode, allStations) : undefined
  return parent?.agglomeration_type === 'geographical' ? parent._u_i_c_station_code : code
}

// Replaces a member stop with its agglomeration; any other station is returned unchanged.
export const resolveSearchStation = <T extends Station>(
  station: T,
  allStations: Station[],
): T | Station => {
  const code = getSearchStationCode(station, allStations)
  if (code === station._u_i_c_station_code) return station
  return findStation(code, allStations) ?? station
}
